import { useEffect, useMemo, useRef, useState } from 'react';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../lib/salutations';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Stepper } from '../../../../components/Stepper';
import { StackedCell } from '../../../../components/table/StackedCell';
import { Tooltip } from '../../../../components/Tooltip';
import { DataTable, type DataColumn } from '../../../../components/data-table/DataTable';
import { FieldGroup } from '../../../../components/inputs/FieldGroup';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { verifyPassword } from '../../../../lib/reauth';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceRequestsApi } from '../../../self-service/evidence-requests/evidenceRequestsApi';
import { evidenceDisposalTypesApi, evidenceItemsApi } from '../evidenceApi';
import {
  CUSTODY,
  CUSTODY_FACET,
  DESTINATION,
  EVIDENCE_EXTENSIONS,
  type EvidenceRequestCaseOption,
  type EvidenceRequestDetail,
  type EvidenceRequestResult,
  type EvidenceRequestableItem,
  type EvidenceTermOption,
  type EvidenceTransferDestination,
  type EvidenceTransferResult,
} from '../evidenceTypes';
import { usePersonName, useStaffOptions } from '../../../human-resources/personnel/useStaff';
import { sampleTransfer, samplePdf } from '../utils/sampleEvidence';
import { EvidenceFilesField } from './EvidenceFilesField';
import { EvidencePlaceField } from './EvidencePlaceField';

/*
  FOUR STEPS — product call, 09 Oct 2026: transfer to · the items · the destination's details · the
  password with a warning. The tick and Submit sit on the last; a refusal goes to the step owning its field.
  A REQUEST stops at the details: nothing moves until a member raises the transfer, with THEIR password.
*/
type Step = 'destination' | 'items' | 'details' | 'confirm';
const TRANSFER_STEPS: Step[] = ['destination', 'items', 'details', 'confirm'];
const REQUEST_STEPS: Step[] = ['destination', 'items', 'details'];
const STEP_LABEL: Record<Step, string> = {
  destination: 'Transfer to',
  items: 'Items',
  details: 'Details',
  confirm: 'Confirm',
};
const FIELD_STEP: Record<string, Step> = {
  evidenceCaseId: 'destination',
  destination: 'destination',
  itemIds: 'items',
  password: 'confirm',
};

const DEFER_REASONS = [
  'Too large for the evidence room',
  'Hazardous — kept at a secure facility',
  'Perishable — kept in cold storage',
  'Vehicle or vessel — kept at the impound yard',
];

/** A disposal type typed into the picker — STAGED, created under its own key on save. */
const PENDING_TYPE = 'pending:disposal-type:';

/** What the items step needs of a stored item — the case's own list, or the request feed's. */
type Candidate = EvidenceRequestableItem;

const DESTINATION_OPTIONS = [
  {
    value: String(DESTINATION.court),
    label: 'Court',
    description: 'Sent to a court under its order. Goes to approval; nothing moves until it is approved.',
  },
  {
    value: String(DESTINATION.disposal),
    label: 'Disposed',
    description: 'Disposed of, by a disposal type, for a reason. Goes to approval; nothing moves until it is approved.',
  },
  {
    value: String(DESTINATION.defer),
    label: 'Defer to other storage',
    description: 'Kept outside the facility, at a named place. Takes effect at once — no approval.',
  },
];

type TransferProps =
  | {
      mode?: 'transfer';
      caseId: string;
      /**
       * The pending TRANSFER request this fulfils (product call, 09 Oct 2026): its destination is the
       * only one offered, its items open selected, and its judge and court order are CARRIED OVER — the
       * judge's PersonId is reused, so no second copy of the name is written.
       */
      fromRequest?: EvidenceRequestDetail | null;
      onClose: () => void;
      onSaved: (result: EvidenceTransferResult, destination: EvidenceTransferDestination) => void;
    }
  | {
      /**
       * RAISE A TRANSFER REQUEST — Self Service (product call, 09 Oct 2026). A case the requester is a
       * member of, then court or disposal (never a deferral — that is the team's own call), the items,
       * the details. No password and no requestor field: the requester IS the requestor.
       */
      mode: 'request';
      onClose: () => void;
      onRequested: (saved: EvidenceRequestResult) => void;
    };

/**
 * The Stored folder's TRANSFER — product calls, 08 Oct 2026. Several STORED items of the case,
 * searched and added to a table, sent to ONE destination:
 *
 * - **Court** — court branch, court order (number and the signed order), and the judge. The judge's
 *   name goes to the identity store; nothing in `rsys_` keeps it. ONE approval for the batch.
 * - **Disposed** — a disposal type (custom-adding) and the reason. ONE approval for the batch.
 * - **Defer to other storage** — the reason and the place outside the facility. Immediate.
 *
 * Every destination asks for the password — evidence leaving the vault, approved or not. It is
 * checked through `verifyPassword` first; what travels is the one-time proof, never the password.
 *
 * <b>The same form RAISES a transfer request</b> from Self Service (`mode="request"`), so a request
 * cannot ask for a transfer the form could not then raise.
 */
export function EvidenceTransferModal(props: TransferProps) {
  const asRequest = props.mode === 'request';
  const fromRequest = props.mode === 'request' ? null : (props.fromRequest ?? null);
  const ID = asRequest ? 'evidence-request-transfer' : 'evidence-transfer';
  const STEPS = asRequest ? REQUEST_STEPS : TRANSFER_STEPS;

  const canCreateType = usePermission('evidence-disposal-types.create');

  // What a request already settled — carried over rather than asked again.
  const reuseJudge = fromRequest?.judgePersonId != null;
  const requestOrderFile = fromRequest?.courtOrderFile ?? null;
  const judgeName = usePersonName(fromRequest?.judgePersonId);

  const [caseId, setCaseId] = useState(props.mode === 'request' ? '' : props.caseId);
  const [stepIndex, setStepIndex] = useState(0);
  const [destination, setDestination] = useState<string>(String(fromRequest?.destination ?? DESTINATION.court));
  const [itemIds, setItemIds] = useState<string[]>([]);
  const [courtBranch, setCourtBranch] = useState(fromRequest?.courtBranch ?? '');
  const [orderNumber, setOrderNumber] = useState(fromRequest?.courtOrderNumber ?? '');
  const [orderFile, setOrderFile] = useState<File[]>([]);
  const [judgeSalutation, setJudgeSalutation] = useState('');
  const [judgeFirst, setJudgeFirst] = useState('');
  const [judgeMiddle, setJudgeMiddle] = useState('');
  const [judgeLast, setJudgeLast] = useState('');
  const [judgeSuffix, setJudgeSuffix] = useState('');
  // The REQUEST's requester is the requestor — product call, 09 Oct 2026.
  const [requestorId, setRequestorId] = useState(fromRequest?.requesterPersonnelId ?? '');
  const { options: staff, loading: loadingStaff } = useStaffOptions(!asRequest);
  const [disposalTypeId, setDisposalTypeId] = useState(fromRequest?.evidenceDisposalTypeId ?? '');
  const [reason, setReason] = useState(fromRequest?.reason ?? '');
  const [place, setPlace] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [cases, setCases] = useState<EvidenceRequestCaseOption[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [disposalTypes, setDisposalTypes] = useState<EvidenceTermOption[]>([]);
  // Which case the candidates were loaded FOR — loading is derived from it, so a new case reads as loading at once.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  /*
    Autogenerate picked the CASE as well — its items are not loaded yet, so the generator asks for them
    to be filled when they arrive. Written in the click handler, read in the load callback; never in render.
  */
  const fillItemsOnLoad = useRef(false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // The catalogs that do not depend on the case.
  useEffect(() => {
    const controller = new AbortController();

    // Failed loads name no field, so they never reach the save path's error state.
    void Promise.all([
      evidenceDisposalTypesApi.options(controller.signal).catch(() => [] as EvidenceTermOption[]),
      asRequest ? evidenceRequestsApi.cases(controller.signal).catch(() => [] as EvidenceRequestCaseOption[]) : Promise.resolve([]),
      asRequest ? Promise.resolve(null) : evidenceItemsApi.eligibility(controller.signal).catch(() => null),
    ]).then(([types, nextCases, eligibility]) => {
      if (controller.signal.aborted) return;
      setDisposalTypes(types);
      setCases(nextCases);
      // THE SIGNED-IN EMPLOYEE requests it by default — only while nobody was chosen.
      const mine = eligibility?.personnelId;
      if (mine) setRequestorId((current) => current || mine);
    });

    return () => controller.abort();
  }, [asRequest]);

  // The case's stored items — the case on screen, or the one a requester picked.
  useEffect(() => {
    // No case yet (a requester has not picked one) — nothing to load.
    if (caseId === '') return;

    const controller = new AbortController();

    const load: Promise<Candidate[]> = asRequest
      ? evidenceRequestsApi.items(caseId, controller.signal)
      : evidenceItemsApi
          .list({ caseId, custody: CUSTODY_FACET[CUSTODY.stored], pageSize: 100, page: 1 }, controller.signal)
          // An item already waiting on a decision is not offered — the server refuses it anyway.
          .then((page) => page.items.filter((i) => i.isActive && i.pendingMovementType == null));

    void load
      .catch(() => [] as Candidate[])
      .then((next) => {
        if (controller.signal.aborted) return;
        setCandidates(next);

        // A request's items open SELECTED — those still in storage with nothing waiting on them.
        if (fromRequest) {
          const offered = new Set(next.map((c) => c.id));
          setItemIds(
            fromRequest.lines
              .map((l) => l.evidenceItemId)
              .filter((id): id is string => id != null && offered.has(id)),
          );
        }

        if (fillItemsOnLoad.current) {
          fillItemsOnLoad.current = false;
          setItemIds(sampleTransfer({ itemIds: next.map((c) => c.id), disposalTypeIds: [] }).itemIds);
        }

        setLoadedFor(caseId);
      });

    return () => controller.abort();
  }, [caseId, asRequest, fromRequest]);

  const loading = caseId !== '' && loadedFor !== caseId;

  const picked = Number(destination) as EvidenceTransferDestination;
  const toCourt = picked === DESTINATION.court;
  const toDisposal = picked === DESTINATION.disposal;

  /*
    OFFER ONLY WHAT CAN BE CHOSEN: a request is never a deferral (the team's own call, immediate), and
    a request being FULFILLED goes where it asked — sending it elsewhere is declining it.
  */
  const destinationOptions = fromRequest
    ? DESTINATION_OPTIONS.filter((o) => o.value === String(fromRequest.destination))
    : asRequest
      ? DESTINATION_OPTIONS.filter((o) => o.value !== String(DESTINATION.defer)).map((o) => ({
          ...o,
          description:
            o.value === String(DESTINATION.court)
              ? "Sent to a court under its order. The case's team raises the transfer; it then goes to approval."
              : "Disposed of, by a disposal type, for a reason. The case's team raises the transfer; it then goes to approval.",
        }))
      : DESTINATION_OPTIONS;

  const chosen = useMemo(() => new Set(itemIds), [itemIds]);
  const byId = useMemo(() => new Map(candidates.map((c) => [c.id, c])), [candidates]);
  const added = itemIds.map((id) => byId.get(id)).filter((c): c is Candidate => c !== undefined);

  /*
    AN OPTION SAYS WHAT THE ITEM IS — the type and description lead, the numbers and where it is
    kept under them (the dropdown rule, 08 Oct 2026). A bare inventory number is useless to pick by.
  */
  const itemOptions = candidates
    .filter((c) => !chosen.has(c.id))
    .map((c) => ({
      value: c.id,
      label: `${c.typeName ?? 'Item'} — ${c.description}`,
      sublabel: [c.inventoryNumber, c.containerNumber ? `Box ${c.containerNumber}` : null, c.storageLocationName]
        .filter(Boolean)
        .join(' · '),
      keywords: [c.inventoryNumber, c.containerNumber ?? '', c.storageLocationName ?? ''].join(' '),
    }));

  const stagedType = disposalTypeId.startsWith(PENDING_TYPE) ? disposalTypeId.slice(PENDING_TYPE.length) : null;
  const typeOptions = [
    ...(stagedType !== null ? [{ value: disposalTypeId, label: stagedType }] : []),
    ...disposalTypes.map((t) => ({ value: t.id, label: t.name })),
  ];

  const detailsGap =
    toCourt
        ? courtBranch.trim() === ''
          ? 'Enter the court branch.'
          : orderNumber.trim() === ''
            ? "Enter the court order's number."
            : orderFile.length === 0 && requestOrderFile === null
              ? 'Attach the court order.'
              : !reuseJudge && (judgeFirst.trim() === '' || judgeLast.trim() === '')
                ? "Enter the judge's first and last name."
                : !asRequest && requestorId === ''
                  ? 'Choose the requestor.'
                  : null
        : toDisposal
          ? disposalTypeId === ''
            ? 'Choose the disposal type.'
            : reason.trim() === ''
              ? 'Give the reason for the disposal.'
              : null
          : reason.trim() === ''
            ? 'Give the reason it is deferred.'
            : place.trim() === ''
              ? 'Enter the transfer-to place.'
              : null;

  const missingOn: Record<Step, string | null> = {
    destination: asRequest && caseId === '' ? 'Choose the case.' : null,
    items: itemIds.length === 0 ? 'Add at least one item.' : null,
    details: detailsGap,
    confirm: asRequest ? null : password.length === 0 ? 'Confirm your password.' : null,
  };
  const currentStep = STEPS[Math.min(stepIndex, STEPS.length - 1)];
  const isLastStep = currentStep === STEPS[STEPS.length - 1];
  const missing = STEPS.map((step) => missingOn[step]).find((m) => m !== null) ?? null;
  const canSave = missing === null && confirmed && !saving;

  function onFailure(caught: unknown) {
    const failure = caught instanceof ApiError ? caught : new ApiError('Could not transfer the evidence.', 0);

    setError(failure);
    focusValidationError(failure, {
      idPrefix: ID,
      // A RadioGroup puts its id on each OPTION, so the field maps to the chosen one.
      fieldIds: { destination: `${ID}-destination-${destination}` },
      stepOf: (field) => STEPS.indexOf(FIELD_STEP[field] ?? 'details'),
      onStep: setStepIndex,
    });
  }

  /** The staged disposal type first, under its OWN key — never folded into the transfer. */
  async function resolveDisposalType(): Promise<string> {
    if (!toDisposal || stagedType === null) return disposalTypeId;

    const created = await evidenceDisposalTypesApi.create({ name: stagedType, description: null });
    setDisposalTypeId(created.id);
    setDisposalTypes((current) => [...current, { id: created.id, name: created.name }]);
    return created.id;
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);
    setPasswordError(null);

    if (props.mode === 'request') {
      try {
        const typeId = await resolveDisposalType();

        const raised = await evidenceRequestsApi.raiseTransfer(
          {
            evidenceCaseId: caseId,
            destination: picked,
            itemIds,
            courtBranch: toCourt ? courtBranch.trim() : null,
            courtOrderNumber: toCourt ? orderNumber.trim() : null,
            judgeSalutation: toCourt ? judgeSalutation.trim() || null : null,
            judgeFirstName: toCourt ? judgeFirst.trim() : null,
            judgeMiddleName: toCourt ? judgeMiddle.trim() || null : null,
            judgeLastName: toCourt ? judgeLast.trim() : null,
            judgeSuffix: toCourt ? judgeSuffix.trim() || null : null,
            evidenceDisposalTypeId: toDisposal ? typeId : null,
            reason: toDisposal ? reason.trim() : null,
          },
          toCourt ? (orderFile[0] ?? null) : null,
        );

        props.onRequested(raised);
      } catch (caught) {
        onFailure(caught);
      } finally {
        setSaving(false);
      }

      return;
    }

    let proof: string;

    try {
      proof = (await verifyPassword(password)).token;
    } catch {
      // The sign-in form's generic answer — nothing about the account's state.
      setPasswordError('That password is not correct.');
      setStepIndex(STEPS.indexOf('confirm'));
      setSaving(false);
      return;
    } finally {
      // Dropped as soon as it has served its purpose — what travels on is the proof.
      setPassword('');
    }

    try {
      const typeId = await resolveDisposalType();

      const result = await evidenceItemsApi.transfer(
        {
          evidenceCaseId: caseId,
          destination: picked,
          itemIds,
          courtBranch: toCourt ? courtBranch.trim() : null,
          courtOrderNumber: toCourt ? orderNumber.trim() : null,
          // A request's judge is carried over by the server — no name is sent, so none is written twice.
          judgeSalutation: judgeSalutation.trim() ? judgeSalutation.trim() : null,
          judgeFirstName: toCourt && !reuseJudge ? judgeFirst.trim() : null,
          judgeMiddleName: toCourt && !reuseJudge ? judgeMiddle.trim() || null : null,
          judgeLastName: toCourt && !reuseJudge ? judgeLast.trim() : null,
          judgeSuffix: toCourt && !reuseJudge ? judgeSuffix.trim() || null : null,
          requestorPersonnelId: toCourt ? requestorId : null,
          evidenceDisposalTypeId: toDisposal ? typeId : null,
          reason: toCourt ? null : reason.trim(),
          place: picked === DESTINATION.defer ? place.trim() : null,
          evidenceRequestId: fromRequest?.id ?? null,
        },
        toCourt ? (orderFile[0] ?? null) : null,
        proof,
      );

      props.onSaved(result, picked);
    } catch (caught) {
      onFailure(caught);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={props.onClose}
      title={asRequest ? 'Transfer request' : 'Transfer evidence'}
      size="2xl"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleTransfer({
            itemIds: candidates.map((c) => c.id),
            disposalTypeIds: disposalTypes.map((t) => t.id),
          });

          // The case is a choice the items hang off — picked only while none is, and its items then load.
          if (asRequest && caseId === '' && cases.length > 0) {
            fillItemsOnLoad.current = true;
            setCaseId(cases[0].id);
          }
          // A request's items, branch, order and disposal are what the requester asked — left alone.
          if (!fromRequest) {
            setItemIds(sample.itemIds);
            setCourtBranch(sample.courtBranch);
            setOrderNumber(sample.courtOrderNumber);
            setDisposalTypeId(sample.disposalTypeId || (canCreateType ? `${PENDING_TYPE}Sample disposal type` : ''));
            setReason(sample.reason);
          }
          if (requestOrderFile === null) setOrderFile([samplePdf('sample-court-order.pdf')]);
          if (!reuseJudge) {
            setJudgeFirst(sample.judgeFirstName);
            setJudgeMiddle(sample.judgeMiddleName);
            setJudgeLast(sample.judgeLastName);
            setJudgeSuffix(sample.judgeSuffix);
          }
          if (!asRequest && !fromRequest && staff.length > 0) setRequestorId(staff[Math.floor(Math.random() * staff.length)].value);
          setPlace(sample.place);
          // Never the password.
        },
        disabled: saving || (loading && caseId !== '') || loadingStaff,
      }}
      confirm={isLastStep ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={props.onClose} disabled={saving}>
            Cancel
          </Button>

          {stepIndex > 0 ? (
            <Button id={`${ID}-back`} variant="secondary" onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={saving}>
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Tooltip disabledReason={missingOn[currentStep]}>
              <Button
                id={`${ID}-next`}
                variant="secondary"
                onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))}
                disabled={missingOn[currentStep] !== null}
              >
                Next
              </Button>
            </Tooltip>
          ) : null}

          <Tooltip disabledReason={missing}>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {fromRequest ? (
          <div role="note" className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs text-slate-600">
              From request <span className="font-mono">{fromRequest.requestNumber}</span>. Its destination, items and
              details are filled in; correct anything before submitting. Submitting raises the transfer and fulfils the
              request.
            </p>
          </div>
        ) : null}

        <Stepper
          id={ID}
          steps={STEPS.map((key) => ({ key, label: STEP_LABEL[key] }))}
          activeIndex={stepIndex}
          onJump={(index) => {
            if (index <= stepIndex || STEPS.slice(0, index).every((step) => missingOn[step] === null)) setStepIndex(index);
          }}
        />

        <div id={`${ID}-panel-${currentStep}`} role="tabpanel" aria-labelledby={`${ID}-step-${currentStep}`} className="flex flex-col gap-4">
          {currentStep === 'destination' ? (
            <>
            {asRequest ? (
              <Select
                id={`${ID}-evidence-case-id`}
                label="Case"
                required
                description="A case you are a member of. Its team raises the transfer — with their own password, and through approval."
                placeholder={cases.length === 0 ? 'You are not a member of any case' : 'Choose a case'}
                value={caseId}
                onChange={(next) => {
                  // The items belong to the case — choosing another clears them (the container-then-member rule).
                  if (next !== caseId) setItemIds([]);
                  setCaseId(next);
                }}
                options={cases.map((c) => ({ value: c.id, label: c.title, sublabel: c.caseNumber, keywords: c.caseNumber }))}
                remote
                disabled={saving || cases.length === 0}
                error={error?.fieldError('evidenceCaseId')}
              />
            ) : null}

            <RadioGroup
              id={`${ID}-destination`}
              label="Transfer to"
              required
              columns={1}
              value={destination}
              onChange={setDestination}
              disabled={saving}
              options={destinationOptions}
              error={error?.fieldError('destination')}
            />
            </>
          ) : currentStep === 'items' ? (
            <>
            <div className="flex flex-col gap-2">
              <Select
                id={`${ID}-item-ids`}
                label="Items"
                required
                description="Search the case's stored evidence and pick each item to add it below. An item already waiting for a decision is not offered."
                placeholder={
                  !loading && candidates.length === 0
                    ? 'No stored item of this case can be transferred'
                    : 'Search items by type, description, inventory or box no.…'
                }
                // Picking ADDS A ROW rather than holding a value — the Share dialog's shape.
                value=""
                onChange={(next) => {
                  if (next && !chosen.has(next)) setItemIds((current) => [...current, next]);
                }}
                options={itemOptions}
                remote
                loading={loading}
                disabled={saving || (!loading && candidates.length === 0)}
                error={error?.fieldError('itemIds')}
              />

              <DataTable<Candidate>
                id={`${ID}-items`}
                columns={ITEM_COLUMNS}
                rows={added}
                rowKey={(row) => row.id}
                sort={null}
                onToggleSort={() => undefined}
                onDelete={saving ? undefined : (row) => setItemIds((current) => current.filter((id) => id !== row.id))}
                deleteLabel={() => 'Remove'}
                emptyMessage="No item added yet. Search above and pick the items to transfer."
              />
            </div>
            </>
          ) : currentStep === 'details' ? (
            <>
            {toCourt ? (
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    id={`${ID}-court-branch`}
                    label="Court Branch"
                    required
                    description="The court receiving the evidence — written as it will stand on each item's chain of custody."
                    value={courtBranch}
                    onChange={(e) => setCourtBranch(e.target.value)}
                    maxLength={200}
                    disabled={saving}
                    error={error?.fieldError('courtBranch')}
                  />

                  <TextField
                    id={`${ID}-court-order-number`}
                    label="Court Order No."
                    required
                    description="The order's number or reference. It is the reason on each item's custody line."
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    maxLength={100}
                    disabled={saving}
                    className="font-mono"
                    error={error?.fieldError('courtOrderNumber')}
                  />
                </div>

                <EvidenceFilesField
                  id={`${ID}-court-order`}
                  label="Court order file"
                  required={requestOrderFile === null}
                  description={
                    requestOrderFile !== null
                      ? `The requester filed ${requestOrderFile} with the request — it is used unless you attach a replacement here.`
                      : 'The signed order. It is attached to every item\'s custody line once the transfer is approved.'
                  }
                  accept={EVIDENCE_EXTENSIONS}
                  files={orderFile}
                  onChange={setOrderFile}
                  disabled={saving}
                  error={error?.fieldError('courtOrder')}
                />

                {/*
                  THE JUDGE — a NAME, so one of the protected values: it is written to the identity store
                  and the transfer keeps only a PersonId. The person's-name row, as the issuance contact's.
                  A request's judge is already there — its name is READ, never typed again.
                */}
                {reuseJudge ? (
                  <PreviewTable
                    variant="table-display"
                    rows={[{ label: 'Judge', value: judgeName ?? '…' }]}
                  />
                ) : (
                  <FormFieldset legend="Judge">
                    <div className="grid gap-4 sm:grid-cols-3">
                      <FieldGroup
                        label="First name"
                        labelFor={`${ID}-judge-first-name`}
                        description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
                        segments={[
                          {
                            id: `${ID}-judge-salutation`,
                            label: 'Salutation',
                            value: judgeSalutation,
                            onChange: setJudgeSalutation,
                            options: SALUTATION_OPTIONS,
                            disabled: saving,
                            // The addon gives way, never the grid — a name row is three equal columns.
                            width: SALUTATION_SEGMENT_WIDTH,
                            emptyLabel: SALUTATION_EMPTY_LABEL,
                          },
                          {
                            id: `${ID}-judge-first-name`,
                            label: 'First name',
                            value: judgeFirst,
                            onChange: setJudgeFirst,
                            required: true,
                            maxLength: 100,
                            error: error?.fieldError('judgeFirstName'),
                            disabled: saving,
                          },
                        ]}
                      />

                      <TextField
                        id={`${ID}-judge-middle-name`}
                        label="Middle name"
                        value={judgeMiddle}
                        onChange={(e) => setJudgeMiddle(e.target.value)}
                        maxLength={100}
                        disabled={saving}
                        error={error?.fieldError('judgeMiddleName')}
                      />

                      <FieldGroup
                        label="Last name"
                        description="The judge's last name, and a suffix if any."
                        segments={[
                          {
                            id: `${ID}-judge-last-name`,
                            label: 'Last name',
                            value: judgeLast,
                            onChange: setJudgeLast,
                            maxLength: 100,
                            required: true,
                            error: error?.fieldError('judgeLastName'),
                            disabled: saving,
                          },
                          {
                            id: `${ID}-judge-suffix`,
                            label: 'Suffix',
                            value: judgeSuffix,
                            onChange: setJudgeSuffix,
                            maxLength: 20,
                            width: 'w-20 shrink-0',
                            error: error?.fieldError('judgeSuffix'),
                            disabled: saving,
                          },
                        ]}
                      />
                    </div>
                  </FormFieldset>
                )}

                {/*
                  THE REQUESTOR, after the judge — product call, 09 Oct 2026: the employee asking for it. A
                  REQUEST has none to ask: the requester is the requestor.
                */}
                {asRequest ? null : (
                  <Select
                    id={`${ID}-requestor-personnel-id`}
                    label="Requestor"
                    required
                    description={
                      fromRequest
                        ? 'The employee requesting the transfer to court. Defaults to whoever raised the request.'
                        : 'The employee requesting the transfer to court. Defaults to you.'
                    }
                    placeholder="Choose an employee"
                    value={requestorId}
                    onChange={setRequestorId}
                    options={staff}
                    remote
                    loading={loadingStaff}
                    disabled={saving}
                    error={error?.fieldError('requestorPersonnelId')}
                  />
                )}
              </div>
            ) : toDisposal ? (
              <div className="flex flex-col gap-4">
                <Select
                  id={`${ID}-evidence-disposal-type-id`}
                  label="Disposal Type"
                  required
                  description={
                    canCreateType
                      ? 'How it is disposed of. Pick one, or type a new one.'
                      : 'How it is disposed of.'
                  }
                  placeholder="Choose a disposal type"
                  value={disposalTypeId}
                  onChange={setDisposalTypeId}
                  options={typeOptions}
                  remote
                  loading={loading}
                  allowCustom={canCreateType}
                  onCreateOption={canCreateType ? (raw) => setDisposalTypeId(`${PENDING_TYPE}${raw.trim()}`) : undefined}
                  disabled={saving}
                  error={error?.fieldError('evidenceDisposalTypeId')}
                />

                <TextField
                  id={`${ID}-reason`}
                  label="Reason"
                  required
                  description="Why it is disposed of. It is written, with the disposal type, on each item's custody line."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={150}
                  disabled={saving}
                  error={error?.fieldError('reason')}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <Select
                  id={`${ID}-reason`}
                  label="Reason"
                  required
                  description="Why it is kept outside the facility. Pick one, or type your own."
                  placeholder="Choose or type a reason"
                  value={reason}
                  onChange={setReason}
                  options={DEFER_REASONS.map((r) => ({ value: r, label: r }))}
                  sort="none"
                  allowCustom
                  disabled={saving}
                  error={error?.fieldError('reason')}
                />

                <EvidencePlaceField
                  id={`${ID}-place`}
                  caseId={caseId}
                  description="Where it is kept outside the facility. Pick a place this case has used, or type a new one."
                  value={place}
                  onChange={setPlace}
                  disabled={saving}
                  error={error?.fieldError('place')}
                />
              </div>
            )}

            {/* A REQUEST's consequence, stated before the tick — the step it is ticked on. */}
            {asRequest ? (
              <p className="text-xs text-slate-500">
                Nothing moves yet: the case&apos;s team sees this request under the case&apos;s Requests, and raises the
                transfer — with their own password, and through approval — or declines it, with a reason.
              </p>
            ) : null}
            </>
          ) : (
            // NARROWER and CENTRED — product call, 09 Oct 2026: the last step is one warning and one field.
            <div className="mx-auto flex w-full max-w-md flex-col gap-4">
            {/*
              THE WARNING before the password — product call, 09 Oct 2026. States what Submit DOES for the
              destination picked, so the tick below is given knowingly.
            */}
            <div role="note" className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-900">
                {picked === DESTINATION.defer
                  ? `${itemIds.length === 1 ? 'This item leaves' : `These ${itemIds.length} items leave`} the evidence room at once.`
                  : `${itemIds.length === 1 ? 'This item' : `These ${itemIds.length} items`} will leave the evidence room once approved.`}
              </p>
              <p className="mt-1 text-xs text-amber-800">
                {picked === DESTINATION.defer
                  ? 'No approval is asked: each item gets a Deferred line on its chain of custody now, and is kept at the place you named.'
                  : picked === DESTINATION.court
                    ? 'One approval covers them all — approved, every one is transferred to the court and the order is attached to each; rejected, none moves.'
                    : 'One approval covers them all — approved, every one is disposed of, which cannot be undone; rejected, none moves.'}
              </p>
            </div>

            <TextField
              id={`${ID}-password`}
              label="Your password"
              required
              type="password"
              autoComplete="current-password"
              description="Confirms it is still you at this machine. It is checked and discarded, never stored; the request carries a one-time confirmation instead."
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(null);
              }}
              disabled={saving}
              error={passwordError ?? error?.fieldError('password')}
            />

            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// What it is, then where it is kept now — the numbers that let the reader check they picked the right one.
const ITEM_COLUMNS: DataColumn<Candidate>[] = [
  { key: 'type', header: 'Type', width: 140, render: (row) => <TextCell value={row.typeName} /> },
  {
    // THE CASE TABLES' EVIDENCE COLUMN — product call, 09 Oct 2026: the description, with the inventory no.
    // and the storage location under it, exactly as the Evidence tab draws it.
    key: 'description',
    header: 'Description',
    width: 360,
    render: (row) => (
      <StackedCell
        primary={row.description}
        secondary={row.storageLocationName ? `${row.inventoryNumber} · ${row.storageLocationName}` : row.inventoryNumber}
        mono
      />
    ),
  },
  { key: 'containerNumber', header: 'Container/Box No', width: 140, render: (row) => <TextCell value={row.containerNumber} mono /> },
];
